import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", cashier: "Cashier", provider: "Provider", sales_manager: "Sales Manager", sales_agent: "Sales Force" };

function NavList({ items, onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-purple-50 text-purple-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
          <Icon size={17} /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function StaffLayout({ items, portal }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/login"); };

  const side = (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex items-center gap-3 px-2">
        <Logo size={40} />
        <div>
          <p className="text-sm font-extrabold text-gradient">GIA CARE+</p>
          <p className="text-[11px] font-medium text-slate-500">{portal}</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto"><NavList items={items} onNavigate={() => setOpen(false)} /></div>
      <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-3">
        <p className="truncate text-sm font-semibold text-slate-800" data-testid="staff-user-name">{user?.full_name}</p>
        <p className="truncate text-[11px] text-slate-500">{ROLE_LABEL[user?.role]} · {user?.username}</p>
        <button className="btn-secondary btn-sm mt-2 w-full" onClick={doLogout} data-testid="logout-button"><LogOut size={14} /> Sign out</button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-slate-200 bg-white p-4 lg:block">{side}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white p-4 shadow-2xl rise">
            <button className="absolute right-3 top-3 rounded-lg p-1.5 hover:bg-slate-100" onClick={() => setOpen(false)} data-testid="close-mobile-nav"><X size={18} /></button>
            {side}
          </aside>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
          <button className="rounded-lg p-1.5 hover:bg-slate-100" onClick={() => setOpen(true)} data-testid="open-mobile-nav"><Menu size={20} /></button>
          <Logo size={30} />
          <span className="text-sm font-extrabold text-gradient">GIA CARE+ · {portal}</span>
        </header>
        <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
